import { makeAutoObservable, reaction, runInAction } from 'mobx';

import { AuthApi } from 'components/auth/auth.api';
import {
  AccountDto,
  SignUpDto,
  SessionInfoDto,
  SignInDto,
  InvitedUser,
  UpdatePersonalInformationResponse,
} from 'components/auth/auth.dto';

import { INTAKE_MEETING_SCHEDULED_STORAGE_KEY } from './auth.constants';

class AuthStore {
  authApi: AuthApi;

  isAuthenticated = false;

  isLoading = false;

  isSuccessValidation: boolean | null = null;

  sessionInfo: SessionInfoDto | null = null;

  account: AccountDto | null = null;

  invitedUserInfo: InvitedUser | null = null;

  intakeMeetingScheduled: boolean = false;

  constructor() {
    this.authApi = new AuthApi();

    const intakeMeetingScheduledState = sessionStorage.getItem(INTAKE_MEETING_SCHEDULED_STORAGE_KEY);

    if (intakeMeetingScheduledState) {
      this.intakeMeetingScheduled = JSON.parse(intakeMeetingScheduledState);
    }

    makeAutoObservable(this, {}, { autoBind: true });

    reaction(
      () => this.intakeMeetingScheduled,
      (intakeMeetingScheduled) =>
        sessionStorage.setItem(INTAKE_MEETING_SCHEDULED_STORAGE_KEY, JSON.stringify(intakeMeetingScheduled)),
    );
  }

  async login(signInDto: SignInDto) {
    this.isLoading = true;

    let sessionInfo: SessionInfoDto | null = null;

    try {
      const sessionInfoDto = await this.authApi.login(signInDto);

      runInAction(() => {
        this.sessionInfo = sessionInfoDto!;
        localStorage.setItem('token', sessionInfoDto!.accessToken);
        localStorage.setItem('refreshToken', sessionInfoDto!.refreshToken);
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }

    return sessionInfo;
  }

  async getAccount() {
    this.isLoading = true;

    let account: AccountDto | null = null;

    try {
      account = await this.authApi.getCurrentAccount();

      runInAction(() => {
        this.account = account;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }

    return account;
  }

  setAuthenticated() {
    this.isAuthenticated = true;
  }

  async logout() {
    this.isLoading = true;

    try {
      await this.authApi.logout();

      runInAction(() => {
        this.isAuthenticated = false;
        this.intakeMeetingScheduled = false;
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async getInvitedUser(inviteToken: string) {
    this.isLoading = true;

    let invitedUserInfo: InvitedUser | null = null;

    try {
      invitedUserInfo = await this.authApi.getInvitedUser(inviteToken);

      runInAction(() => {
        this.invitedUserInfo = invitedUserInfo;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }

    return invitedUserInfo;
  }

  async signUp(signUpDto: SignUpDto) {
    this.isLoading = true;

    try {
      const sessionInfoDto = await this.authApi.signUp(signUpDto);

      runInAction(() => {
        this.sessionInfo = sessionInfoDto!;
        localStorage.setItem('token', sessionInfoDto!.accessToken);
        localStorage.setItem('refreshToken', sessionInfoDto!.refreshToken);
        this.isAuthenticated = true;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async sendVerificationCode() {
    this.isLoading = true;

    try {
      await this.authApi.sendEmailVerificationCode();
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isSuccessValidation = null;
      });
    }
  }

  async verifyVerificationCode(code: string) {
    this.isLoading = true;

    try {
      const { success } = await this.authApi.verifyEmailVerificationCode(code);

      runInAction(() => {
        this.isSuccessValidation = success;
      });

      return { success: success };
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async requestResetPassword(email: string) {
    this.isLoading = true;

    try {
      return await this.authApi.requestResetPassword(email);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async resetPassword(password: string, token: string) {
    this.isLoading = true;

    try {
      return await this.authApi.resetPassword(password, token);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async updatePassword(newPassword: string, oldPassword: string) {
    this.isLoading = true;

    try {
      return await this.authApi.updatePassword({ oldPassword: oldPassword, newPassword: newPassword });
    } catch (err) {
      return { success: false };
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async verifyPassword(password: string) {
    this.isLoading = true;

    try {
      return await this.authApi.createPasswordValidationToken(password);
    } catch (err) {
      return { success: false };
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async sendUpdateEmailVerificationCode(email: string, accountPasswordValidationToken: string) {
    this.isLoading = true;

    try {
      await this.authApi.sendUpdateEmailVerificationCode({
        email: email,
        accountPasswordValidationToken: accountPasswordValidationToken,
      });

      return { success: true };
    } catch (err) {
      return { success: false };
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isSuccessValidation = null;
      });
    }
  }

  async updateEmailAddressByVerifyingCode(email: string, code: string) {
    this.isLoading = true;

    try {
      const { success } = await this.authApi.updateEmailAddressByVerifyingCode(email, code);

      runInAction(() => {
        this.isSuccessValidation = success;
      });

      return { success: success };
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async updatePersonalInformation(body: UpdatePersonalInformationResponse) {
    this.isLoading = true;

    try {
      await this.authApi.updatePersonalInformation(body);
      await this.getAccount();
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  setIntakeMeetingScheduled(value: boolean) {
    runInAction(() => {
      this.intakeMeetingScheduled = value;
    });
  }
}

export default AuthStore;
